// MUD play panel — scrolling log + room view + exit buttons + command input.
// Visible only when phase = "playing".

import type { Net } from "../net.js";
import type { ClientState, ClientStore, LogTab } from "../state.js";
import { classifyLogTab } from "../state.js";
import type { Direction } from "../../shared/protocol.js";
import { getSettings, onSettings } from "./settings.js";
import { openFriendsPanel } from "./friends.js";
import { openLibraryPanel } from "./library.js";
import { openCottagePanel } from "./cottage.js";
import { openGuildPanel } from "./guild.js";
import { openCasesPanel } from "./cases.js";
import { openEconomyPanel } from "./economy.js";
import { openHelpPanel } from "./help.js";

const DIR_ALIASES: Record<string, Direction> = {
  n: "n", north: "n", s: "s", south: "s", e: "e", east: "e", w: "w", west: "w",
  ne: "ne", northeast: "ne", nw: "nw", northwest: "nw",
  se: "se", southeast: "se", sw: "sw", southwest: "sw",
  u: "up", up: "up", d: "down", down: "down",
  in: "in", out: "out",
};

export function renderMudPanel(host: HTMLElement, net: Net, store: ClientStore): void {
  host.innerHTML = `
    <div class="mud-layout">
      <nav class="mud-sidebar" id="sidebar">
        <button class="sb-toggle" id="sb-toggle" title="Toggle sidebar">☰</button>
        <button class="sb-btn" data-action="quests" title="Quests">📋</button>
        <button class="sb-btn" data-action="inventory" title="Inventory">🎒</button>
        <button class="sb-btn" data-action="skills" title="Skills">⚔️</button>
        <button class="sb-btn" data-action="alchemy" title="Alchemy">⚗️</button>
        <button class="sb-btn" data-action="cases" title="Accusations">⚖️</button>
        <button class="sb-btn" data-action="market" title="Market">🏪</button>
        <button class="sb-btn" data-action="guild" title="Guild">⛪</button>
        <button class="sb-btn" data-action="library" title="Library">📖</button>
        <button class="sb-btn" data-action="divine" title="Divination">🔮</button>
        <button class="sb-btn" data-action="friends" title="Friends">👥</button>
        <button class="sb-btn" data-action="cottage" title="Home">🏠</button>
        <button class="sb-btn" data-action="settings" title="Settings">⚙️</button>
        <button class="sb-btn" data-action="help" title="Help">❓</button>
      </nav>
      <main class="mud-main">
        <section class="mud-room">
          <h2 id="room-name">…</h2>
          <div class="room-illustration" id="room-illustration"></div>
          <p class="room-desc" id="room-desc"></p>
          <div class="room-npcs" id="room-npcs"></div>
          <div class="room-mobs" id="room-mobs"></div>
          <div class="room-items" id="room-items"></div>
          <div class="room-others" id="room-others"></div>
          <div class="exits" id="exits"></div>
          <div class="portals" id="portals"></div>
        </section>
        <section class="mud-log">
          <div class="log-tabs" id="log-tabs">
            <button class="log-tab active" data-tab="all">All</button>
            <button class="log-tab" data-tab="events">Events</button>
            <button class="log-tab" data-tab="combat">Combat</button>
            <button class="log-tab" data-tab="npc">NPC</button>
            <button class="log-tab" data-tab="chat">Chat</button>
          </div>
          <div class="log" id="log"></div>
          <div class="new-msg-indicator" id="new-msg" style="display:none" title="Click to scroll to latest">↓ New messages</div>
          <div class="cmdline">
            <input id="cmd" type="text" placeholder="type 'look', 'inv', 'talk margthe', 'n', 'say hi', '/help'" autocomplete="off" />
          </div>
        </section>
      </main>
    </div>
    <div id="dialogue-overlay"></div>
    <div id="inventory-overlay"></div>
  `;

  const elName    = host.querySelector<HTMLElement>("#room-name")!;
  const elIllust  = host.querySelector<HTMLElement>("#room-illustration")!;
  const elDesc    = host.querySelector<HTMLElement>("#room-desc")!;
  const elNpcs    = host.querySelector<HTMLElement>("#room-npcs")!;
  const elMobs    = host.querySelector<HTMLElement>("#room-mobs")!;
  const elItems   = host.querySelector<HTMLElement>("#room-items")!;
  const elOthers  = host.querySelector<HTMLElement>("#room-others")!;
  const elExits   = host.querySelector<HTMLElement>("#exits")!;
  const elPortals = host.querySelector<HTMLElement>("#portals")!;
  const elLog     = host.querySelector<HTMLElement>("#log")!;
  const elCmd     = host.querySelector<HTMLInputElement>("#cmd")!;
  const elOverlay = host.querySelector<HTMLElement>("#dialogue-overlay")!;
  const elInv     = host.querySelector<HTMLElement>("#inventory-overlay")!;
  const newMsgIndicator = host.querySelector<HTMLElement>("#new-msg")!;

  // "New messages" indicator — click to scroll to bottom
  newMsgIndicator.addEventListener("click", () => {
    elLog.scrollTop = elLog.scrollHeight;
    newMsgIndicator.style.display = "none";
  });

  // Tabbed log state
  let activeTab: LogTab = "all";
  const tabUnread: Record<LogTab, number> = { all: 0, events: 0, combat: 0, npc: 0, chat: 0 };
  const tabButtons = host.querySelectorAll<HTMLElement>(".log-tab");
  for (const btn of tabButtons) {
    btn.addEventListener("click", () => {
      activeTab = (btn.dataset.tab ?? "all") as LogTab;
      tabUnread[activeTab] = 0;
      for (const b of tabButtons) {
        b.classList.toggle("active", b.dataset.tab === activeTab);
        b.classList.remove("has-new");
      }
      repaint(store.get());
    });
  }

  // Sidebar toggle
  const sidebar = host.querySelector<HTMLElement>("#sidebar")!;
  const sbToggle = host.querySelector<HTMLElement>("#sb-toggle")!;
  let sidebarExpanded = localStorage.getItem("hoo_sidebar") !== "collapsed";
  sidebar.classList.toggle("collapsed", !sidebarExpanded);
  sbToggle.addEventListener("click", () => {
    sidebarExpanded = !sidebarExpanded;
    sidebar.classList.toggle("collapsed", !sidebarExpanded);
    localStorage.setItem("hoo_sidebar", sidebarExpanded ? "expanded" : "collapsed");
  });

  // Sidebar buttons — open actual UI panels, not just send messages
  const uiLayer = document.getElementById("ui-layer")!;
  const sidebarActions: Record<string, () => void> = {
    quests:    () => { net.send({ t: "quests" }); void import("./quest_log.js").then((m) => m.openQuestLog(uiLayer, net, store)); },
    inventory: () => net.send({ t: "inventory" }),  // inventory opens via state.inventory_open
    skills:    () => { net.send({ t: "skill_tree" }); void import("./skill_tree.js").then((m) => m.openSkillTree(uiLayer, net, store)); },
    alchemy:   () => { net.send({ t: "craft_skills" }); void import("./crafting_panel.js").then((m) => m.openCraftingPanel(net, store)); },
    cases:     () => openCasesPanel(net, store),
    market:    () => openEconomyPanel(net, store, "auction"),
    guild:     () => openGuildPanel(store, net),
    library:   () => openLibraryPanel(store, net),
    divine:    () => net.send({ t: "divine" }),
    friends:   () => openFriendsPanel(store, net),
    cottage:   () => openCottagePanel(store, net),
    settings:  () => { const sh = document.getElementById("settings-overlay"); if (sh) void import("./settings.js").then((m) => m.renderSettings(sh)); },
    help:      () => openHelpPanel(uiLayer),
  };
  for (const btn of host.querySelectorAll<HTMLElement>(".sb-btn")) {
    btn.addEventListener("click", () => {
      const action = btn.dataset.action;
      if (action && sidebarActions[action]) sidebarActions[action]();
    });
  }

  function repaint(state: ClientState): void {
    const settings = getSettings();
    // Minimap visibility toggle (settings-driven, applied to DOM host)
    const mm = document.getElementById("minimap-host");
    if (mm) mm.style.display = settings.show_minimap ? "" : "none";
    // 3D scene backdrop — hide by zero'ing the scene-layer host when the
    // user wants a clean MUD layout.
    const sceneLayer = document.getElementById("scene-layer");
    if (sceneLayer) sceneLayer.style.display = settings.show_scene_3d ? "" : "none";
    if (state.room) {
      elName.textContent = state.room.name;
      elIllust.innerHTML = settings.show_room_illustrations && state.room.illustration_url
        ? `<img src="${state.room.illustration_url}" alt="${escape(state.room.name)}" loading="lazy" />`
        : "";
      elDesc.textContent = state.room.description;
      const npcs = state.room.npcs ?? [];
      elNpcs.innerHTML = npcs.length
        ? `<span class="dim">You see:</span> ${npcs.map((n) =>
            (settings.show_portraits && n.portrait_url)
              ? `<button class="npc-tag npc-tag-with-portrait" data-id="${n.id}"><img src="${n.portrait_url}" alt="" loading="lazy" />${escape(n.name)}</button>`
              : `<button class="npc-tag" data-id="${n.id}">${escape(n.name)}</button>`
          ).join(" ")}`
        : "";
      for (const b of elNpcs.querySelectorAll<HTMLButtonElement>("button.npc-tag")) {
        b.addEventListener("click", () => net.send({ t: "talk", npc_id_or_name: Number(b.dataset.id) }));
      }
      // Mobs — render each as a styled "threat" chip. Click = attack/engage.
      const mobs = state.room.mobs ?? [];
      elMobs.innerHTML = mobs.length
        ? `<span class="dim">Hostile:</span> ${mobs.map((m) => {
            const portrait = (settings.show_portraits && m.portrait_url)
              ? `<img class="mob-portrait" src="${m.portrait_url}" alt="" loading="lazy" />` : "";
            return `<button class="mob-tag" data-id="${m.id}" title="Click to attack — HP ${m.hp}/${m.hp_max}">${portrait}⚔ ${escape(m.name)} <span class="dim">${m.hp}/${m.hp_max}</span></button>`;
          }).join(" ")}`
        : "";
      for (const b of elMobs.querySelectorAll<HTMLButtonElement>("button.mob-tag")) {
        b.addEventListener("click", () => net.send({ t: "attack", mob_id_or_name: Number(b.dataset.id) }));
      }
      const floor = state.room.items ?? [];
      elItems.innerHTML = floor.length
        ? `<span class="dim">On the ground:</span> ${floor.map((i) => {
            const icon = (settings.show_item_icons && i.icon_url)
              ? `<img class="item-icon" src="${i.icon_url}" alt="" loading="lazy" />` : "";
            return `<button class="item-tag" data-id="${i.id}">${icon}${escape(i.name)}${i.qty > 1 ? " ×" + i.qty : ""}</button>`;
          }).join(" ")}`
        : "";
      for (const b of elItems.querySelectorAll<HTMLButtonElement>("button.item-tag")) {
        b.addEventListener("click", () => net.send({ t: "get", what: Number(b.dataset.id) }));
      }
      elOthers.innerHTML = state.room.others.length
        ? `<span class="dim">Also here:</span> ${state.room.others.map(escape).join(", ")}`
        : "";
      elExits.innerHTML = settings.show_exit_buttons
        ? state.room.exits.map((e) =>
            `<button data-dir="${e.dir}" class="exit ${e.locked ? "locked" : ""}">${escape(e.name)}</button>`
          ).join("")
        : "";
      for (const b of elExits.querySelectorAll<HTMLButtonElement>("button.exit")) {
        b.addEventListener("click", () => net.send({ t: "move", dir: b.dataset.dir as Direction }));
      }
      // Portals — inter-zone travel. Each portal is a styled button with the
      // target zone name + level/faction gate indicator. Click = portal msg.
      const portals = state.room.portals ?? [];
      elPortals.innerHTML = portals.length === 0 ? "" : `
        <div class="portals-header"><span class="dim">Portals from here:</span></div>
        ${portals.map((p) => {
          const tgt = escape(p.target_zone_name ?? p.target_zone);
          const lvl = p.level_req ? ` · lvl ${p.level_req}+` : "";
          const fac = p.faction_req ? ` · ${escape(p.faction_req)}-only` : "";
          return `<button class="portal" data-pid="${p.id}" title="${escape(p.description ?? "")}">→ ${tgt}<span class="dim">${lvl}${fac}</span></button>`;
        }).join("")}
      `;
      for (const b of elPortals.querySelectorAll<HTMLButtonElement>("button.portal")) {
        b.addEventListener("click", () => net.send({ t: "portal", portal_id: Number(b.dataset.pid) }));
      }
    }

    // Dialogue overlay (grammar gate: only visible while talking)
    if (state.dialogue) {
      const d = state.dialogue;
      const portrait = (settings.show_portraits && d.portrait_url)
        ? `<img class="dialogue-portrait" src="${d.portrait_url}" alt="${escape(d.npc_name)}" loading="lazy" />`
        : "";
      elOverlay.innerHTML = `
        <div class="dialogue-modal">
          <div class="dialogue-header">
            ${portrait}
            <h3>${escape(d.npc_name)}</h3>
          </div>
          <p class="dialogue-text">${escape(d.text)}</p>
          <div class="dialogue-options">
            ${d.options.map((o) =>
              `<button data-i="${o.index}" class="dial-opt ${o.ends ? "ends" : ""}">${o.index + 1}. ${escape(o.text)}</button>`
            ).join("")}
          </div>
        </div>
      `;
      for (const b of elOverlay.querySelectorAll<HTMLButtonElement>("button.dial-opt")) {
        b.addEventListener("click", () => net.send({ t: "reply", option_index: Number(b.dataset.i) }));
      }
    } else {
      elOverlay.innerHTML = "";
    }

    if (state.inventory_open) {
      const items = state.inventory;
      elInv.innerHTML = `
        <div class="inv-overlay"><div class="inv-modal">
          <h3>Inventory <button id="inv-close" class="inv-close">×</button></h3>
          ${items.length === 0 ? `<p class="dim">Empty.</p>` : `
            <ul class="inv-list">
              ${items.map((i) => `
                <li>
                  ${(settings.show_item_icons && i.icon_url) ? `<img class="inv-icon" src="${i.icon_url}" alt="" loading="lazy" />` : (settings.show_item_icons ? `<span class="inv-icon inv-icon-placeholder">${escape(i.category?.[0] ?? "?")}</span>` : "")}
                  <span class="big">${escape(i.name)}</span>
                  <span class="dim">${escape(i.category)}${i.equipped_slot ? " — equipped: " + escape(i.equipped_slot) : ""}${i.qty > 1 ? " (×" + i.qty + ")" : ""}</span>
                  <span class="actions">
                    ${i.slot && !i.equipped_slot ? `<button data-equip="${i.id}">equip</button>` : ""}
                    ${i.equipped_slot ? `<button data-unequip="${escape(i.equipped_slot)}">unequip</button>` : ""}
                    <button data-drop="${i.id}">drop</button>
                  </span>
                </li>
              `).join("")}
            </ul>
          `}
        </div></div>
      `;
      // Close on clicking backdrop or close button
      elInv.querySelector<HTMLElement>(".inv-overlay")?.addEventListener("click", (ev) => { if ((ev.target as HTMLElement).classList.contains("inv-overlay")) store.toggleInventory(false); });
      elInv.querySelector<HTMLButtonElement>("#inv-close")?.addEventListener("click", () => store.toggleInventory(false));
      for (const b of elInv.querySelectorAll<HTMLButtonElement>("button[data-equip]")) {
        b.addEventListener("click", () => net.send({ t: "equip", instance_id: Number(b.dataset.equip) }));
      }
      for (const b of elInv.querySelectorAll<HTMLButtonElement>("button[data-unequip]")) {
        b.addEventListener("click", () => net.send({ t: "unequip", slot: b.dataset.unequip! }));
      }
      for (const b of elInv.querySelectorAll<HTMLButtonElement>("button[data-drop]")) {
        b.addEventListener("click", () => net.send({ t: "drop", instance_id: Number(b.dataset.drop) }));
      }
    } else {
      elInv.innerHTML = "";
    }

    const verbosity = settings.chat_verbosity;
    const allEntries = state.log.slice(-200).filter((e) => {
      if (verbosity === "quiet" && e.kind === "system") return false;
      return true;
    });
    // Filter by active tab
    const entries = allEntries.filter((e) => {
      if (activeTab === "all") return true;
      return classifyLogTab(e) === activeTab;
    });
    // Update unread badges on inactive tabs
    for (const e of allEntries.slice(-5)) {
      const tab = classifyLogTab(e);
      if (tab !== activeTab && tab !== "all") {
        tabUnread[tab]++;
        const tabBtn = host.querySelector<HTMLElement>(`.log-tab[data-tab="${tab}"]`);
        if (tabBtn && !tabBtn.classList.contains("active")) tabBtn.classList.add("has-new");
      }
    }
    // Check if user has scrolled up before repaint
    const wasAtBottom = elLog.scrollTop + elLog.clientHeight >= elLog.scrollHeight - 30;
    elLog.innerHTML = entries.map((e) => {
      const cls = e.kind;
      const who = e.who ? `<span class="who">${escape(e.who)}:</span> ` : "";
      if (e.voyn_font) {
        const variantClass = `voyn voyn-${e.voyn_font}`;
        const qAttr = e.voyn_quality !== undefined ? ` data-quality="${e.voyn_quality}"` : "";
        return `<div class="log-line ${cls}">${who}<span class="${variantClass}"${qAttr}>${escape(e.text)}</span></div>`;
      }
      return `<div class="log-line ${cls}">${who}${escape(e.text)}</div>`;
    }).join("");
    // Only auto-scroll if user was already at the bottom
    if (wasAtBottom) {
      elLog.scrollTop = elLog.scrollHeight;
      newMsgIndicator.style.display = "none";
    } else if (entries.length > 0) {
      newMsgIndicator.style.display = "block";
    }
  }

  store.subscribe(repaint);
  onSettings(() => repaint(store.get()));
  repaint(store.get());

  elCmd.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter") return;
    const raw = elCmd.value.trim();
    if (!raw) return;
    elCmd.value = "";
    store.log({ kind: "command", text: `> ${raw}` });
    handleCommand(raw, net, store);
  });

  elCmd.focus();

  // Keyboard shortcuts: WASD + arrow keys = movement when input not focused
  document.addEventListener("keydown", (ev) => {
    // Don't capture keys when user is typing in ANY input, textarea, or select
    const tag = (document.activeElement as HTMLElement)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const map: Record<string, Direction> = {
      w: "n", ArrowUp: "n", s: "s", ArrowDown: "s",
      a: "w", ArrowLeft: "w", d: "e", ArrowRight: "e",
      q: "nw", e: "ne", z: "sw", c: "se",
    };
    const dir = map[ev.key];
    if (dir) {
      ev.preventDefault();
      net.send({ t: "move", dir });
    } else if (ev.key === "l" || ev.key === "L") {
      net.send({ t: "look" });
    } else if (ev.key === "/" || ev.key === "Escape") {
      // `/` anywhere auto-focuses the command input and starts a command
      ev.preventDefault();
      elCmd.focus();
      if (ev.key === "/") elCmd.value = "/";
    }
  });
}

function handleCommand(raw: string, net: Net, store: ClientStore): void {
  // /-prefix and word commands both work; first token decides.
  const stripped = raw.startsWith("/") ? raw.slice(1) : raw;
  // Alias expansion: if the first token matches one of the player's aliases,
  // substitute the expansion. If the expansion has ';', it's a macro — split
  // and dispatch each sub-command in order. Aliases cannot recurse.
  const aliases = store.get().aliases;
  const firstToken = stripped.split(/\s+/)[0]?.toLowerCase() ?? "";
  if (firstToken && aliases[firstToken]) {
    const remainder = stripped.slice(firstToken.length).trim();
    const expansion = aliases[firstToken];
    if (expansion.includes(";")) {
      const parts = expansion.split(";").map((s) => s.trim()).filter(Boolean);
      let i = 0;
      const fire = () => {
        if (i >= parts.length) return;
        const next = remainder ? `${parts[i]} ${remainder}` : parts[i];
        i++;
        runExpanded(next, net, store);
        // Small delay between macro steps so the server sees them serialized.
        if (i < parts.length) setTimeout(fire, 60);
      };
      fire();
      return;
    }
    const expanded = remainder ? `${expansion} ${remainder}` : expansion;
    runExpanded(expanded, net, store);
    return;
  }
  runExpanded(stripped, net, store);
}

function runExpanded(stripped: string, net: Net, store: ClientStore): void {
  const [head, ...rest] = stripped.split(/\s+/);
  const arg = rest.join(" ");
  const lower = head.toLowerCase();

  if (lower === "look" || lower === "l") return net.send({ t: "look" });
  if (lower === "quit" || lower === "logout") {
    net.send({ t: "logout" });
    store.reset();
    return;
  }
  if (lower === "say" || lower === "'" ) return net.send({ t: "say", text: arg });
  if (lower === "talk" || lower === "t" || lower === "speak") {
    if (!arg) return store.log({ kind: "system", text: "Talk to whom? Try: talk margthe" });
    return net.send({ t: "talk", npc_id_or_name: arg });
  }
  if (lower === "bye" || lower === "leave" || lower === "endtalk") return net.send({ t: "end_talk" });
  if (lower === "settings" || lower === "options") {
    const host = document.getElementById("settings-overlay");
    if (host) { void import("./settings.js").then((m) => m.renderSettings(host)); }
    return;
  }
  if (lower === "who" || lower === "online") return net.send({ t: "who" });
  if (lower === "inv" || lower === "inventory" || lower === "i") return net.send({ t: "inventory" });
  if (lower === "get" || lower === "take" || lower === "pickup") {
    if (!arg) return store.log({ kind: "system", text: "Get what? Try: get dagger" });
    return net.send({ t: "get", what: arg });
  }
  if (lower === "drop") {
    const id = Number.parseInt(arg, 10);
    if (!Number.isFinite(id)) return store.log({ kind: "system", text: "drop <instance_id> — open inventory to find ids" });
    return net.send({ t: "drop", instance_id: id });
  }
  if (lower in DIR_ALIASES) return net.send({ t: "move", dir: DIR_ALIASES[lower] });
  if (lower === "switch" || lower === "switchchar" || lower === "characters") {
    return net.send({ t: "switch_character" });
  }
  if (lower === "me") {
    if (!arg) return store.log({ kind: "system", text: "/me <action> — e.g. /me bows deeply (Patron only for freeform)" });
    return net.send({ t: "custom_emote", action: arg });
  }
  if (lower === "title") {
    // `/title` alone clears the title; `/title <text>` sets it.
    return net.send({ t: "set_title", title: arg });
  }
  if (lower === "home") return net.send({ t: "home" });
  if (lower === "place") {
    if (!arg) return store.log({ kind: "system", text: "/place <instance_id> [note]" });
    const parts = arg.split(/\s+/);
    const inst = Number.parseInt(parts[0], 10);
    if (!Number.isFinite(inst)) return store.log({ kind: "system", text: "/place <instance_id> [note]" });
    const note = parts.slice(1).join(" ");
    return net.send({ t: "cottage_place", instance_id: inst, note });
  }
  if (lower === "unplace" || lower === "retrieve") {
    const id = Number.parseInt(arg, 10);
    if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/unplace <furniture_id>" });
    return net.send({ t: "cottage_remove", furniture_id: id });
  }
  if (lower === "cottage") return net.send({ t: "cottage_list" });
  if (lower === "accuse") {
    // /accuse <target> <witchcraft|heresy|corruption|blasphemy|possession> [-m=maleficium] [reason]
    const parts = arg.split(/\s+/);
    const target = parts[0];
    const type = (parts[1] ?? "").toLowerCase();
    const validTypes = ["witchcraft", "heresy", "corruption", "blasphemy", "possession"];
    if (!target || !validTypes.includes(type)) {
      return store.log({ kind: "system", text: "/accuse <target> <witchcraft|heresy|corruption|blasphemy|possession> [reason...]" });
    }
    let jurisdiction: "hammer" | "maleficium" = "hammer";
    const rest: string[] = [];
    for (const p of parts.slice(2)) {
      if (p === "-m" || p === "--maleficium") jurisdiction = "maleficium";
      else rest.push(p);
    }
    return net.send({
      t: "accuse", target,
      accusation_type: type as "witchcraft" | "heresy" | "corruption" | "blasphemy" | "possession",
      jurisdiction, text: rest.join(" "),
    });
  }
  if (lower === "evidence_voynich" || lower === "inscribe") {
    // /inscribe <case_id> <voynich text ...>
    const parts = arg.split(/\s+/);
    const caseId = Number.parseInt(parts[0], 10);
    const voynich = parts.slice(1).join(" ");
    if (!Number.isFinite(caseId) || !voynich) {
      return store.log({ kind: "system", text: "/inscribe <case_id> <voynich text>  — files voynich text as DOCUMENTARY evidence" });
    }
    return net.send({ t: "evidence_voynich", accusation_id: caseId, voynich_text: voynich });
  }
  if (lower === "evidence" || lower === "ev") {
    // /evidence <case_id> <type> <name> [-- description]
    const parts = arg.split(/\s+/);
    const caseId = Number.parseInt(parts[0], 10);
    const type = (parts[1] ?? "").toLowerCase();
    const validE = ["physical", "testimonial", "supernatural", "circumstantial", "documentary"];
    if (!Number.isFinite(caseId) || !validE.includes(type)) {
      return store.log({ kind: "system", text: "/evidence <case_id> <physical|testimonial|supernatural|circumstantial|documentary> <name> [-- description]" });
    }
    const nameAndDesc = parts.slice(2).join(" ").split(/\s*--\s*/);
    const name = nameAndDesc[0] ?? "";
    const description = nameAndDesc[1] ?? "";
    if (!name) return store.log({ kind: "system", text: "Evidence needs a name." });
    return net.send({ t: "evidence", accusation_id: caseId, evidence_type: type as "physical" | "testimonial" | "supernatural" | "circumstantial" | "documentary", name, description });
  }
  if (lower === "testify") {
    // /testify <case_id> <character|expert|eyewitness> <text>
    const parts = arg.split(/\s+/);
    const caseId = Number.parseInt(parts[0], 10);
    const type = (parts[1] ?? "").toLowerCase();
    if (!Number.isFinite(caseId) || !["character", "expert", "eyewitness"].includes(type)) {
      return store.log({ kind: "system", text: "/testify <case_id> <character|expert|eyewitness> <statement>" });
    }
    const text = parts.slice(2).join(" ");
    if (!text) return store.log({ kind: "system", text: "Testimony requires a statement." });
    return net.send({ t: "testify", accusation_id: caseId, testimony_type: type as "character" | "expert" | "eyewitness", text });
  }
  if (lower === "cases") {
    if (!arg) {
      void import("./cases.js").then((m) => m.openCasesPanel(net, store));
      return;
    }
    const f = arg.toLowerCase();
    const valid = ["all", "mine", "against_me", "active"];
    if (!valid.includes(f)) return store.log({ kind: "system", text: "/cases [all|mine|against_me|active]" });
    return net.send({ t: "cases", filter: f as "all" | "mine" | "against_me" | "active" });
  }
  if (lower === "case") {
    const id = Number.parseInt(arg, 10);
    if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/case <id>" });
    return net.send({ t: "case_detail", accusation_id: id });
  }
  if (lower === "appeal") {
    const id = Number.parseInt(arg, 10);
    if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/appeal <case_id>" });
    return net.send({ t: "appeal", accusation_id: id });
  }
  if (lower === "rank" || lower === "inquisitor") return net.send({ t: "inquisitor_status" });
  if (lower === "cast") {
    if (!arg) return store.log({ kind: "system", text: "/cast <text> [section=H|A|P|S|Z] [font=regular|3d|italic|leftalic|3d-italic|3d-leftalic]" });
    let section: "H" | "A" | "P" | "S" | "Z" | undefined;
    let fontVariant: "regular" | "3d" | "italic" | "leftalic" | "3d-italic" | "3d-leftalic" | undefined;
    const words: string[] = [];
    for (const tok of arg.split(/\s+/)) {
      const s = tok.match(/^section=([HAPSZ])$/i);
      const f = tok.match(/^font=(regular|3d|italic|leftalic|3d-italic|3d-leftalic)$/i);
      if (s) section = s[1].toUpperCase() as "H" | "A" | "P" | "S" | "Z";
      else if (f) fontVariant = f[1].toLowerCase() as "regular" | "3d" | "italic" | "leftalic" | "3d-italic" | "3d-leftalic";
      else words.push(tok);
    }
    return net.send({ t: "cast", text: words.join(" "), section, font_variant: fontVariant });
  }
  if (lower === "decode") {
    if (!arg) return store.log({ kind: "system", text: "/decode <voynich text>" });
    return net.send({ t: "decode", text: arg });
  }
  if (lower === "learn" || lower === "study") {
    if (!arg) return store.log({ kind: "system", text: "/learn <glyph>  — e.g. /learn qo, /learn ar" });
    return net.send({ t: "learn_glyph", glyph: arg });
  }
  if (lower === "mastery") return net.send({ t: "mastery" });
  if (lower === "glyphs") return net.send({ t: "glyphs" });
  if (lower === "market" || lower === "post" || lower === "economy") {
    const tab = arg.toLowerCase();
    void import("./economy.js").then((m) => m.openEconomyPanel(net, store, (tab === "auction" || tab === "bank") ? tab : "mail"));
    return;
  }
  if (lower === "rites" || lower === "altar") {
    void import("./coven_ritual.js").then((m) => m.openCovenRitualPanel(net, store));
    return;
  }
  if (lower === "workbench" || lower === "scribe") {
    void import("./casting.js").then((m) => m.openCastingPanel(net, store));
    return;
  }
  if (lower === "register_agent" || lower === "agent_register") {
    const parts = arg.split(/\s+/);
    const real = parts[0]?.toLowerCase();
    const apparent = parts[1]?.toLowerCase();
    const key = parts.slice(2).join(" ");
    if ((real !== "hammer" && real !== "maleficium") ||
        (apparent !== "hammer" && apparent !== "maleficium" && apparent !== "neutral") ||
        key.length < 6) {
      return store.log({ kind: "system", text: "/register_agent <hammer|maleficium> <hammer|maleficium|neutral> <secret_key_6chars+>" });
    }
    return net.send({ t: "register_agent", real_faction: real as "hammer" | "maleficium", apparent_faction: apparent as "hammer" | "maleficium" | "neutral", secret_key: key });
  }
  if (lower === "unregister_agent" || lower === "agent_unregister") return net.send({ t: "unregister_agent" });
  if (lower === "disguise") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "disguise_list" });
    if (subLower === "apply") {
      if (!subArg) return store.log({ kind: "system", text: "/disguise apply <template_id>" });
      return net.send({ t: "disguise_apply", template_id: subArg });
    }
    if (subLower === "remove") return net.send({ t: "disguise_remove" });
    return store.log({ kind: "system", text: "Try: /disguise list | apply <id> | remove" });
  }
  if (lower === "moon") return net.send({ t: "moon" });
  if (lower === "standing" || lower === "standings") {
    const f = arg.toLowerCase();
    if (f && f !== "hammer" && f !== "maleficium") return store.log({ kind: "system", text: "/standing [hammer|maleficium]" });
    return net.send({ t: "standing", faction: (f || undefined) as "hammer" | "maleficium" | undefined });
  }
  if (lower === "betray") {
    const parts = arg.split(/\s+/);
    const from = parts[0]?.toLowerCase();
    const to = parts[1]?.toLowerCase();
    if ((from !== "hammer" && from !== "maleficium") || (to !== "hammer" && to !== "maleficium")) {
      return store.log({ kind: "system", text: "/betray <hammer|maleficium> <hammer|maleficium>" });
    }
    return net.send({ t: "betray", from_faction: from as "hammer" | "maleficium", to_faction: to as "hammer" | "maleficium" });
  }
  if (lower === "report") {
    const parts = arg.split(/\s+/);
    const target = parts[0];
    const category = (parts[1] ?? "").toLowerCase();
    const text = parts.slice(2).join(" ");
    const validCats = ["harassment", "cheating", "bug", "spam", "other"];
    if (!target || !validCats.includes(category) || !text) {
      return store.log({ kind: "system", text: "/report <target> <harassment|cheating|bug|spam|other> <description>" });
    }
    return net.send({ t: "report", target, category: category as "harassment" | "cheating" | "bug" | "spam" | "other", text });
  }
  if (lower === "friend" || lower === "friends") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") { net.send({ t: "friend_list" }); return openFriendsPanel(store, net); }
    if (subLower === "add" || subLower === "request") {
      if (!subArg) return store.log({ kind: "system", text: "/friend add <name>" });
      return net.send({ t: "friend_request", target: subArg });
    }
    if (subLower === "accept") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/friend accept <char_id>" });
      return net.send({ t: "friend_accept", friend_char_id: id });
    }
    if (subLower === "decline") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/friend decline <char_id>" });
      return net.send({ t: "friend_decline", friend_char_id: id });
    }
    if (subLower === "remove") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/friend remove <char_id>" });
      return net.send({ t: "friend_remove", friend_char_id: id });
    }
    return store.log({ kind: "system", text: "Try: /friend list | add <name> | accept <id> | decline <id> | remove <id>" });
  }
  if (lower === "daily") {
    const subLower = (rest[0] ?? "").toLowerCase();
    if (subLower === "claim") return net.send({ t: "daily_claim" });
    return net.send({ t: "daily_status" });
  }
  if (lower === "library" || lower === "lib") {
    const subLower = (rest[0] ?? "").toLowerCase();
    if (!subLower || subLower === "list" || subLower === "catalog") {
      net.send({ t: "library_catalog" });
      return openLibraryPanel(store, net);
    }
    if (subLower === "open") {
      const id = Number(rest[1] ?? 0);
      if (!id) return store.log({ kind: "system", text: "/library open <book_id>" });
      net.send({ t: "library_open", book_id: id });
      return openLibraryPanel(store, net);
    }
    if (subLower === "page") {
      const page = Number(rest[1] ?? 0);
      const cur = store.get().library.page;
      if (!cur || !page) return store.log({ kind: "system", text: "/library page <n> — opens a book first" });
      return net.send({ t: "library_page", book_id: cur.book.id, page_no: page });
    }
    if (subLower === "next" || subLower === "prev") {
      const cur = store.get().library.page;
      if (!cur) return store.log({ kind: "system", text: "No book open. /library open <id>" });
      const target = subLower === "next" ? cur.page_no + 1 : cur.page_no - 1;
      if (target < 1 || target > cur.book.page_count) return store.log({ kind: "system", text: "End of book." });
      return net.send({ t: "library_page", book_id: cur.book.id, page_no: target });
    }
    if (subLower === "search") {
      const q = rest.slice(1).join(" ").trim();
      if (q.length < 2) return store.log({ kind: "system", text: "/library search <query> (2+ chars)" });
      const cur = store.get().library.page;
      if (cur) return net.send({ t: "library_search", book_id: cur.book.id, query: q });
      return net.send({ t: "library_search", query: q });
    }
    return store.log({ kind: "system", text: "Try: /library list | open <id> | page <n> | next | prev | search <q>" });
  }
  if (lower === "achievements" || lower === "ach") return net.send({ t: "achievements" });
  if (lower === "tutorial") {
    const subLower = (rest[0] ?? "").toLowerCase();
    if (subLower === "skip") return net.send({ t: "tutorial_skip" });
    if (subLower === "reward" || subLower === "claim") return net.send({ t: "tutorial_claim" });
    return net.send({ t: "tutorial_status" });
  }
  if (lower === "duel") {
    const subLower = (rest[0] ?? "").toLowerCase();
    const subArg = rest.slice(1).join(" ");
    if (!rest.length || subLower === "challenge" || subLower === "invite") {
      const t = rest.length ? rest.slice(subLower === "challenge" || subLower === "invite" ? 1 : 0).join(" ") : "";
      if (!t) return store.log({ kind: "system", text: "/duel <name>" });
      return net.send({ t: "duel_invite", target: t });
    }
    if (subLower === "accept") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/duel accept <duel_id>" });
      return net.send({ t: "duel_accept", duel_id: id });
    }
    if (subLower === "decline") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/duel decline <duel_id>" });
      return net.send({ t: "duel_decline", duel_id: id });
    }
    if (subLower === "act" || subLower === "action") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const action = (parts[1] ?? "").toLowerCase();
      if (!Number.isFinite(id) || !["strike", "parry", "rest"].includes(action)) {
        return store.log({ kind: "system", text: "/duel act <duel_id> <strike|parry|rest>" });
      }
      return net.send({ t: "duel_action", duel_id: id, action: action as "strike" | "parry" | "rest" });
    }
    if (subLower === "forfeit" || subLower === "flee") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/duel forfeit <duel_id>" });
      return net.send({ t: "duel_forfeit", duel_id: id });
    }
    if (subLower === "record" || subLower === "stats") return net.send({ t: "duel_record" });
    return store.log({ kind: "system", text: "Try: /duel <name> | accept <id> | decline <id> | act <id> <strike|parry|rest> | forfeit <id> | record" });
  }
  if (lower === "dq" || lower === "dailyquest" || lower === "dailyquests") {
    const subLower = (rest[0] ?? "").toLowerCase();
    const subArg = rest.slice(1).join(" ");
    if (!rest.length || subLower === "list") return net.send({ t: "daily_quests" });
    if (subLower === "turn_in" || subLower === "turnin" || subLower === "claim") {
      if (!subArg) return store.log({ kind: "system", text: "/dq turn_in <quest_id>" });
      return net.send({ t: "daily_quest_turn_in", quest_id: subArg });
    }
    return store.log({ kind: "system", text: "Try: /dq list | turn_in <id>" });
  }
  if (lower === "block") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "block_list" });
    if (subLower === "add") {
      if (!subArg) return store.log({ kind: "system", text: "/block add <name>" });
      return net.send({ t: "block_add", target: subArg });
    }
    if (subLower === "remove") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/block remove <char_id>" });
      return net.send({ t: "block_remove", char_id: id });
    }
    return store.log({ kind: "system", text: "Try: /block list | add <name> | remove <id>" });
  }
  if (lower === "primi" || lower === "craft") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "primi_list" });
    if (subLower === "craft") {
      const parts = subArg.split(/\s+/);
      const recipe = parts[0];
      const voy = parts.slice(1).join(" ");
      if (!recipe || !voy) return store.log({ kind: "system", text: "/primi craft <recipe_id> <voynich text>" });
      return net.send({ t: "primi_craft", recipe_id: recipe, voynich_text: voy });
    }
    return store.log({ kind: "system", text: "Try: /primi list | craft <recipe_id> <voynich text>" });
  }
  if (lower === "exorcise" || lower === "exorcism") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (subLower === "begin" || subLower === "start") {
      if (!subArg) return store.log({ kind: "system", text: "/exorcise begin <target>" });
      return net.send({ t: "exorcism_begin", target: subArg });
    }
    if (subLower === "pray" || subLower === "exhort" || subLower === "banish") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: `/exorcise ${subLower} <exorcism_id>` });
      return net.send({ t: "exorcism_action", id, action: subLower as "pray" | "exhort" | "banish" });
    }
    if (subLower === "flee") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/exorcise flee <id>" });
      return net.send({ t: "exorcism_flee", id });
    }
    return store.log({ kind: "system", text: "Try: /exorcise begin <target> | pray <id> | exhort <id> | banish <id> | flee <id>" });
  }
  if (lower === "prayer") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "prayer_list" });
    if (subLower === "start") {
      const parts = subArg.split(/\s+/);
      const tplId = parts[0];
      const target = parts[1] ? Number.parseInt(parts[1], 10) : undefined;
      if (!tplId) return store.log({ kind: "system", text: "/prayer start <template_id> [target_case_id]" });
      return net.send({ t: "prayer_start", template_id: tplId, target_accusation_id: target });
    }
    if (subLower === "join") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/prayer join <id>" });
      return net.send({ t: "prayer_join", prayer_id: id });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/prayer cancel <id>" });
      return net.send({ t: "prayer_cancel", prayer_id: id });
    }
    return store.log({ kind: "system", text: "Try: /prayer list | start <template> [target] | join <id> | cancel <id>" });
  }
  if (lower === "ritual") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "ritual_list" });
    if (subLower === "start") {
      const parts = subArg.split(/\s+/);
      const tplId = parts[0];
      const target = parts[1] ? Number.parseInt(parts[1], 10) : undefined;
      if (!tplId) return store.log({ kind: "system", text: "/ritual start <template_id> [target_case_id]" });
      return net.send({ t: "ritual_start", template_id: tplId, target_accusation_id: target });
    }
    if (subLower === "join") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/ritual join <id>" });
      return net.send({ t: "ritual_join", ritual_id: id });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/ritual cancel <id>" });
      return net.send({ t: "ritual_cancel", ritual_id: id });
    }
    return store.log({ kind: "system", text: "Try: /ritual list | start <template> [target] | join <id> | cancel <id>" });
  }
  if (lower === "coven") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "info" || subLower === "who") return net.send({ t: "coven_info" });
    if (subLower === "found" || subLower === "create") {
      if (!subArg) return store.log({ kind: "system", text: "/coven found <name>" });
      return net.send({ t: "coven_found", name: subArg });
    }
    if (subLower === "invite") {
      if (!subArg) return store.log({ kind: "system", text: "/coven invite <char>" });
      return net.send({ t: "coven_invite", target: subArg });
    }
    if (subLower === "accept") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/coven accept <invite_id>" });
      return net.send({ t: "coven_accept", invite_id: id });
    }
    if (subLower === "decline") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/coven decline <invite_id>" });
      return net.send({ t: "coven_decline", invite_id: id });
    }
    if (subLower === "leave") return net.send({ t: "coven_leave" });
    if (subLower === "kick") {
      if (!subArg) return store.log({ kind: "system", text: "/coven kick <char>" });
      return net.send({ t: "coven_kick", target: subArg });
    }
    if (subLower === "promote") {
      const [tgt, rankStr] = subArg.split(/\s+/);
      const rank = (rankStr ?? "master").toLowerCase();
      if (!tgt || (rank !== "master" && rank !== "initiate")) {
        return store.log({ kind: "system", text: "/coven promote <char> <master|initiate>" });
      }
      return net.send({ t: "coven_promote", target: tgt, rank: rank as "master" | "initiate" });
    }
    return store.log({ kind: "system", text: "Try: /coven found|invite|accept|decline|leave|kick|promote|info" });
  }
  if (lower === "bank" || lower === "vault") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") {
      // /bank list [tab] [sort=name|qty|time]
      const parts = subArg.split(/\s+/);
      const tab = parts[0] ? Number.parseInt(parts[0], 10) : undefined;
      let sortBy: "name" | "qty" | "time" | undefined;
      for (const p of parts.slice(1)) {
        const m = p.match(/^sort\s*=\s*(name|qty|time)$/i);
        if (m) sortBy = m[1].toLowerCase() as "name" | "qty" | "time";
      }
      return net.send({ t: "bank_list", tab: Number.isFinite(tab) ? tab : undefined, sort_by: sortBy });
    }
    if (subLower === "tab" || subLower === "label") {
      const parts = subArg.split(/\s+/);
      const tab = Number.parseInt(parts[0], 10);
      const label = parts.slice(1).join(" ");
      if (!Number.isFinite(tab) || !label) return store.log({ kind: "system", text: "/bank tab <n> <label>" });
      return net.send({ t: "bank_set_label", tab, label });
    }
    if (subLower === "deposit" || subLower === "dep") {
      const parts = subArg.split(/\s+/);
      const inst = Number.parseInt(parts[0], 10);
      const tab = parts[1] ? Number.parseInt(parts[1], 10) : 1;
      const qty = parts[2] ? Number.parseInt(parts[2], 10) : undefined;
      if (!Number.isFinite(inst)) return store.log({ kind: "system", text: "/bank deposit <instance_id> [tab] [qty]" });
      return net.send({ t: "bank_deposit", instance_id: inst, tab, qty });
    }
    if (subLower === "withdraw" || subLower === "wd") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const qty = parts[1] ? Number.parseInt(parts[1], 10) : undefined;
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/bank withdraw <bank_id> [qty]" });
      return net.send({ t: "bank_withdraw", bank_id: id, qty });
    }
    return store.log({ kind: "system", text: "Try: /bank list [tab] | deposit <inst> [tab] [qty] | withdraw <id> [qty]" });
  }
  if (lower === "tokens" || lower === "wallet_tokens") return net.send({ t: "tokens" });
  if (lower === "ah" || lower === "auction") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "browse" || subLower === "search") {
      return net.send({ t: "auction_browse", query: subArg || undefined });
    }
    if (subLower === "mine" || subLower === "my") return net.send({ t: "auction_mine" });
    if (subLower === "list" || subLower === "post") {
      // /ah list <instance_id> <qty> <price> [buyout] [reserve]
      const parts = subArg.split(/\s+/);
      const inst = Number.parseInt(parts[0], 10);
      const qty = Number.parseInt(parts[1] ?? "1", 10);
      const price = Number.parseInt(parts[2] ?? "0", 10);
      const buyout = parts[3] ? Number.parseInt(parts[3], 10) : 0;
      const reserve = parts[4] ? Number.parseInt(parts[4], 10) : 0;
      if (!Number.isFinite(inst) || !Number.isFinite(qty) || !Number.isFinite(price) || price <= 0) {
        return store.log({ kind: "system", text: "/ah list <instance_id> <qty> <price> [buyout] [reserve]" });
      }
      return net.send({
        t: "auction_list", instance_id: inst, qty, asking_gold: price,
        buyout_gold: buyout > 0 ? buyout : undefined,
        reserve_gold: reserve > 0 ? reserve : undefined,
      });
    }
    if (subLower === "bid") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const amt = Number.parseInt(parts[1] ?? "0", 10);
      if (!Number.isFinite(id) || !Number.isFinite(amt) || amt <= 0) {
        return store.log({ kind: "system", text: "/ah bid <listing_id> <gold>" });
      }
      return net.send({ t: "auction_bid", listing_id: id, bid_gold: amt });
    }
    if (subLower === "buy") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/ah buy <listing_id>" });
      return net.send({ t: "auction_buy", listing_id: id });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/ah cancel <listing_id>" });
      return net.send({ t: "auction_cancel", listing_id: id });
    }
    return store.log({ kind: "system", text: "Try: /ah browse [query] | mine | list <instance_id> <qty> <price> | buy <id> | cancel <id>" });
  }
  if (lower === "mail" || lower === "m") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "inbox" || subLower === "in") return net.send({ t: "mail_inbox" });
    if (subLower === "sent" || subLower === "outbox") return net.send({ t: "mail_sent_box" });
    if (subLower === "read") {
      const [idStr, ...accRest] = subArg.split(/\s+/);
      const id = Number.parseInt(idStr, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/mail read <id> [accept]" });
      const accept = accRest.join(" ").toLowerCase() === "accept";
      return net.send({ t: "mail_read", mail_id: id, accept_cod: accept });
    }
    if (subLower === "delete" || subLower === "del") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/mail delete <id>" });
      return net.send({ t: "mail_delete", mail_id: id });
    }
    if (subLower === "send") {
      // Format: /mail send <to> <subject> | <body> [| gold=N] [| cod=N]
      const parts = subArg.split("|").map((s) => s.trim());
      const [head, body = "", ...extras] = parts;
      const [to, ...subjectParts] = head.split(/\s+/);
      if (!to || subjectParts.length === 0) return store.log({ kind: "system", text: "/mail send <to> <subject> | <body> [| gold=N] [| cod=N]" });
      const subject = subjectParts.join(" ");
      let gold = 0, cod = 0, itemInst = 0, itemQty = 1;
      for (const ex of extras) {
        const g = ex.match(/^gold\s*=\s*(\d+)$/i);
        const c = ex.match(/^cod\s*=\s*(\d+)$/i);
        const i = ex.match(/^item\s*=\s*(\d+)(?:\s*x\s*(\d+))?$/i);
        if (g) gold = Number(g[1]);
        else if (c) cod = Number(c[1]);
        else if (i) { itemInst = Number(i[1]); itemQty = i[2] ? Number(i[2]) : 1; }
      }
      return net.send({ t: "mail_send", to, subject, body, gold, cod_gold: cod, item_instance_id: itemInst, item_qty: itemQty });
    }
    return store.log({ kind: "system", text: "Try: /mail inbox | sent | read <id> [accept] | send <to> <subject> | <body> [| gold=N] [| cod=N] | delete <id>" });
  }
  if (lower === "tiers" || lower === "subscribe" || lower === "pledge") {
    void import("./tiers.js").then((m) => m.renderTiersPanel(store));
    return;
  }
  if (lower === "rename") {
    if (!arg) return store.log({ kind: "system", text: "/rename <new_name> — Adventurer: 1/yr, Patron: unlimited" });
    return net.send({ t: "rename", new_name: arg });
  }
  if (lower === "tenure" || lower === "veteran") {
    if (!arg) return net.send({ t: "tenure" });
    const id = Number.parseInt(arg, 10);
    if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/tenure — status | /tenure <milestone_days> — claim" });
    return net.send({ t: "tenure_claim", milestone_id: id });
  }
  if (lower === "leave_home" || lower === "leavehome" || lower === "outside") return net.send({ t: "leave_home" });
  if (lower === "visit") {
    if (!arg) return store.log({ kind: "system", text: "/visit <host_name>" });
    return net.send({ t: "cottage_visit", host: arg });
  }
  if (lower === "invite") {
    if (!arg) return store.log({ kind: "system", text: "/invite <character_name>" });
    return net.send({ t: "cottage_invite", guest: arg });
  }
  if (lower === "uninvite") {
    if (!arg) return store.log({ kind: "system", text: "/uninvite <character_name>" });
    return net.send({ t: "cottage_uninvite", guest: arg });
  }
  if (lower === "guests") return net.send({ t: "cottage_guests" });
  if (lower === "house" || lower === "cottage") {
    const subLower = (rest[0] ?? "").toLowerCase();
    const subArg = rest.slice(1).join(" ");
    if (subLower === "upgrade") return net.send({ t: "house_upgrade" });
    if (subLower === "status") return net.send({ t: "house_status" });
    if (subLower === "theme") {
      if (!subArg || subArg === "list") return net.send({ t: "house_theme_list" });
      return net.send({ t: "house_theme_set", theme_id: subArg });
    }
    // Default: open the UI panel (which also pulls status/list/guests).
    return openCottagePanel(store, net);
  }
  if (lower === "guild_panel" || lower === "gpanel") return openGuildPanel(store, net);
  if (lower === "guild" || lower === "g") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (subLower === "found" || subLower === "create") {
      if (!subArg) return store.log({ kind: "system", text: "/guild found <name>" });
      return net.send({ t: "guild_found", name: subArg });
    }
    if (subLower === "invite" || subLower === "inv") {
      if (!subArg) return store.log({ kind: "system", text: "/guild invite <character>" });
      return net.send({ t: "guild_invite", target: subArg });
    }
    if (subLower === "accept") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/guild accept <guild_id>" });
      return net.send({ t: "guild_accept", guild_id: id });
    }
    if (subLower === "decline") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/guild decline <guild_id>" });
      return net.send({ t: "guild_decline", guild_id: id });
    }
    if (subLower === "leave") return net.send({ t: "guild_leave" });
    if (subLower === "" || subLower === "panel") return openGuildPanel(store, net);
    if (subLower === "info" || subLower === "who") return net.send({ t: "guild_info" });
    if (subLower === "claim") return net.send({ t: "territory_claim" });
    if (subLower === "contest") return net.send({ t: "territory_contest" });
    if (subLower === "territory" || subLower === "zones") return net.send({ t: "territory_list" });
    if (subLower === "bank" || subLower === "vault") {
      const parts = subArg.split(/\s+/);
      const subSub = (parts[0] ?? "list").toLowerCase();
      if (subSub === "list" || subSub === "") {
        const tab = parts[1] ? Number.parseInt(parts[1], 10) : undefined;
        return net.send({ t: "guild_bank_list", tab: Number.isFinite(tab) ? tab : undefined });
      }
      if (subSub === "deposit" || subSub === "dep") {
        const inst = Number.parseInt(parts[1], 10);
        const tab = parts[2] ? Number.parseInt(parts[2], 10) : 1;
        const qty = parts[3] ? Number.parseInt(parts[3], 10) : undefined;
        if (!Number.isFinite(inst)) return store.log({ kind: "system", text: "/guild bank deposit <instance_id> [tab] [qty]" });
        return net.send({ t: "guild_bank_deposit", instance_id: inst, tab, qty });
      }
      if (subSub === "withdraw" || subSub === "wd") {
        const id = Number.parseInt(parts[1], 10);
        if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/guild bank withdraw <row_id>" });
        return net.send({ t: "guild_bank_withdraw", bank_id: id });
      }
      return store.log({ kind: "system", text: "Try: /guild bank list [tab] | deposit <inst> [tab] [qty] | withdraw <id>" });
    }
    if (subLower === "hall") return net.send({ t: "guild_hall_enter" });
    if (subLower === "leave_hall" || subLower === "leavehall") return net.send({ t: "guild_hall_leave" });
    if (subLower === "achievements" || subLower === "ach") return net.send({ t: "guild_achievements" });
    if (subLower === "alliance" || subLower === "ally") {
      const parts = subArg.split(/\s+/);
      const op = (parts[0] ?? "").toLowerCase();
      if (!op || op === "list") return net.send({ t: "alliance_list" });
      if (op === "propose") {
        const name = parts.slice(1).join(" ");
        if (!name) return store.log({ kind: "system", text: "/guild alliance propose <target_guild>" });
        return net.send({ t: "alliance_propose", partner_guild_name: name });
      }
      if (op === "accept" || op === "decline" || op === "dissolve") {
        const id = Number.parseInt(parts[1], 10);
        if (!Number.isFinite(id)) return store.log({ kind: "system", text: `/guild alliance ${op} <id>` });
        if (op === "accept") return net.send({ t: "alliance_accept", alliance_id: id });
        if (op === "decline") return net.send({ t: "alliance_decline", alliance_id: id });
        return net.send({ t: "alliance_dissolve", alliance_id: id });
      }
      return store.log({ kind: "system", text: "Try: /guild alliance list | propose <target> | accept <id> | decline <id> | dissolve <id>" });
    }
    if (subLower === "rank_label" || subLower === "ranklabel") {
      const parts = subArg.split(/\s+/);
      const rank = parts[0] as "founder" | "officer" | "member";
      const label = parts.slice(1).join(" ");
      if (!["founder", "officer", "member"].includes(rank) || !label) return store.log({ kind: "system", text: "/guild rank_label <founder|officer|member> <display_name>" });
      return net.send({ t: "guild_rank_label", rank, label });
    }
    if (subLower === "war") {
      const parts = subArg.split(/\s+/);
      const op = (parts[0] ?? "").toLowerCase();
      if (!op || op === "list") return net.send({ t: "guild_war_list" });
      if (op === "declare") {
        const rest2 = parts.slice(1).join(" ");
        const sp = rest2.indexOf(" ");
        if (sp < 0) return store.log({ kind: "system", text: "/guild war declare <target_guild> <reason>" });
        return net.send({ t: "guild_war_declare", target_guild_name: rest2.slice(0, sp), reason: rest2.slice(sp + 1) });
      }
      if (op === "accept") {
        const id = Number.parseInt(parts[1], 10);
        if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/guild war accept <id>" });
        return net.send({ t: "guild_war_accept", war_id: id });
      }
      if (op === "surrender") {
        const id = Number.parseInt(parts[1], 10);
        if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/guild war surrender <id>" });
        return net.send({ t: "guild_war_surrender", war_id: id });
      }
      return store.log({ kind: "system", text: "Try: /guild war list | declare <target> <reason> | accept <id> | surrender <id>" });
    }
    return store.log({ kind: "system", text: "Try: /guild found|invite|accept|decline|leave|info|bank|hall|leave_hall" });
  }
  if (lower === "bestiary" || lower === "best") return net.send({ t: "bestiary" });
  if (lower === "respec") {
    if (!confirm("Respec all skills for 500g + 1 token? Cooldown 7 days.")) return;
    return net.send({ t: "skill_respec" });
  }
  if (lower === "pvp") {
    const subLower = (rest[0] ?? "").toLowerCase();
    if (subLower === "on" || subLower === "flag") return net.send({ t: "pvp_flag", on: true });
    if (subLower === "off" || subLower === "unflag") return net.send({ t: "pvp_flag", on: false });
    return net.send({ t: "pvp_status" });
  }
  if (lower === "seasons" || lower === "calendar") return net.send({ t: "seasons" });
  if (lower === "events" || lower === "world_events") return net.send({ t: "world_events" });
  if (lower === "raid" || lower === "raids") return net.send({ t: "raid_list" });
  if (lower === "gather") {
    const prof = (arg || "").toLowerCase();
    if (prof && !["fishing", "mining", "herbalism", "logging"].includes(prof)) {
      return store.log({ kind: "system", text: "/gather [fishing|mining|herbalism|logging]" });
    }
    return net.send({ t: "gather", profession: prof as "fishing" | "mining" | "herbalism" | "logging" | undefined });
  }
  if (lower === "professions" || lower === "gather_skills") return net.send({ t: "gather_skills" });
  if (lower === "craft") {
    if (!arg) return store.log({ kind: "system", text: "/craft <recipe_id>" });
    return net.send({ t: "craft", recipe_id: arg });
  }
  if (lower === "recipes") {
    const prof = (arg || "smithing").toLowerCase();
    if (!["smithing", "alchemy", "cooking", "tailoring"].includes(prof)) {
      return store.log({ kind: "system", text: "/recipes <smithing|alchemy|cooking|tailoring>" });
    }
    return net.send({ t: "craft_recipes", profession: prof as "smithing" | "alchemy" | "cooking" | "tailoring" });
  }
  if (lower === "craft_skills") return net.send({ t: "craft_skills" });
  if (lower === "shop") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "browse") return net.send({ t: "shop_browse" });
    if (subLower === "mine") return net.send({ t: "shop_mine" });
    if (subLower === "open") {
      const parts = subArg.split(/\s+/);
      const till = Number.parseInt(parts[0] ?? "100", 10);
      const name = parts.slice(1).join(" ");
      if (!name || !Number.isFinite(till) || till < 100) return store.log({ kind: "system", text: "/shop open <initial_till_>=100> <name…>" });
      return net.send({ t: "shop_open", name, initial_till: till });
    }
    if (subLower === "close") {
      if (!confirm("Close your shop? Stock + till will be returned.")) return;
      return net.send({ t: "shop_close" });
    }
    if (subLower === "list" || subLower === "stock") {
      const parts = subArg.split(/\s+/);
      const inst = Number.parseInt(parts[0], 10);
      const qty = Number.parseInt(parts[1], 10);
      const price = Number.parseInt(parts[2], 10);
      if (!Number.isFinite(inst) || !Number.isFinite(qty) || !Number.isFinite(price)) return store.log({ kind: "system", text: "/shop list <instance_id> <qty> <price>" });
      return net.send({ t: "shop_stock_add", instance_id: inst, qty, price });
    }
    if (subLower === "unlist" || subLower === "remove") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/shop unlist <stock_id>" });
      return net.send({ t: "shop_stock_remove", stock_id: id });
    }
    if (subLower === "withdraw") {
      const amt = Number.parseInt(subArg, 10);
      if (!Number.isFinite(amt) || amt <= 0) return store.log({ kind: "system", text: "/shop withdraw <amount>" });
      return net.send({ t: "shop_withdraw", amount: amt });
    }
    if (subLower === "buy") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const qty = Number.parseInt(parts[1] ?? "1", 10);
      if (!Number.isFinite(id) || !Number.isFinite(qty)) return store.log({ kind: "system", text: "/shop buy <stock_id> <qty>" });
      return net.send({ t: "shop_buy", stock_id: id, qty });
    }
    return store.log({ kind: "system", text: "Try: /shop browse | mine | open <till> <name> | close | list <inst> <qty> <price> | unlist <id> | withdraw <amt> | buy <stock_id> <qty>" });
  }
  if (lower === "craft_order" || lower === "co") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "craft_order_list", filter: "open" });
    if (subLower === "mine") return net.send({ t: "craft_order_list", filter: "mine_client" });
    if (subLower === "as_crafter") return net.send({ t: "craft_order_list", filter: "mine_crafter" });
    if (subLower === "commit") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/co commit <order_id>" });
      return net.send({ t: "craft_order_commit", order_id: id });
    }
    if (subLower === "deliver") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/co deliver <order_id>" });
      return net.send({ t: "craft_order_deliver", order_id: id });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/co cancel <order_id>" });
      return net.send({ t: "craft_order_cancel", order_id: id });
    }
    if (subLower === "post") {
      // /co post <output_tid> <output_qty> <bounty> <ing1_tid:qty,ing2_tid:qty> <note…>
      const parts = subArg.split(/\s+/);
      const tid = Number.parseInt(parts[0], 10);
      const qty = Number.parseInt(parts[1], 10);
      const bounty = Number.parseInt(parts[2], 10);
      const ingSpec = parts[3] ?? "";
      const note = parts.slice(4).join(" ");
      if (!Number.isFinite(tid) || !Number.isFinite(qty) || !Number.isFinite(bounty) || !ingSpec) {
        return store.log({ kind: "system", text: "/co post <output_tid> <output_qty> <bounty> <ing_tid:qty,ing_tid:qty,…> <note>" });
      }
      const ingredients = ingSpec.split(",").map((s) => {
        const [t, q] = s.split(":");
        return { template_id: Number.parseInt(t, 10), qty: Number.parseInt(q, 10) };
      }).filter((i) => Number.isFinite(i.template_id) && Number.isFinite(i.qty) && i.qty > 0);
      if (!ingredients.length) return store.log({ kind: "system", text: "invalid ingredient list" });
      return net.send({ t: "craft_order_post", output_template_id: tid, output_qty: qty, bounty_gold: bounty, ingredients, note });
    }
    return store.log({ kind: "system", text: "Try: /co list | mine | as_crafter | post <tid> <qty> <bounty> <ings> <note> | commit <id> | deliver <id> | cancel <id>" });
  }
  if (lower === "market") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (subLower === "book") {
      const tid = Number.parseInt(subArg, 10);
      if (!Number.isFinite(tid)) return store.log({ kind: "system", text: "/market book <template_id>" });
      return net.send({ t: "market_book", template_id: tid });
    }
    if (subLower === "buy" || subLower === "sell") {
      const parts = subArg.split(/\s+/);
      const tid = Number.parseInt(parts[0], 10);
      const qty = Number.parseInt(parts[1] ?? "1", 10);
      const price = Number.parseInt(parts[2] ?? "0", 10);
      if (!Number.isFinite(tid) || qty <= 0 || price <= 0) return store.log({ kind: "system", text: `/market ${subLower} <template_id> <qty> <price_per_unit>` });
      return net.send({ t: "market_place", side: subLower as "buy" | "sell", template_id: tid, price, qty });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/market cancel <order_id>" });
      return net.send({ t: "market_cancel", order_id: id });
    }
    if (subLower === "mine") return net.send({ t: "market_mine" });
    if (subLower === "history") {
      const tid = Number.parseInt(subArg, 10);
      if (!Number.isFinite(tid)) return store.log({ kind: "system", text: "/market history <template_id>" });
      return net.send({ t: "market_history", template_id: tid });
    }
    if (subLower === "trend") {
      const parts = subArg.split(/\s+/);
      const tid = Number.parseInt(parts[0], 10);
      const days = Number.parseInt(parts[1] ?? "7", 10);
      if (!Number.isFinite(tid)) return store.log({ kind: "system", text: "/market trend <template_id> [days]" });
      return net.send({ t: "market_trend", template_id: tid, days });
    }
    return store.log({ kind: "system", text: "Try: /market book <tid> | buy <tid> <qty> <price> | sell <tid> <qty> <price> | mine | cancel <id> | history <tid> | trend <tid>" });
  }
  if (lower === "dungeon" || lower === "dungeons") {
    const subLower = (rest[0] ?? "").toLowerCase();
    const subArg = rest.slice(1).join(" ");
    if (!subLower || subLower === "list") return net.send({ t: "dungeon_list" });
    if (subLower === "enter") {
      if (!subArg) return store.log({ kind: "system", text: "/dungeon enter <id>" });
      return net.send({ t: "dungeon_enter", dungeon_id: subArg });
    }
    if (subLower === "lockouts") return net.send({ t: "dungeon_lockouts" });
    return store.log({ kind: "system", text: "Try: /dungeon list | enter <id> | lockouts" });
  }
  if (lower === "lfg") {
    const subLower = (rest[0] ?? "").toLowerCase();
    const subArg = rest.slice(1).join(" ");
    if (!subLower || subLower === "list") return net.send({ t: "lfg_list" });
    if (subLower === "join") {
      if (!subArg) return store.log({ kind: "system", text: "/lfg join <activity_id>" });
      return net.send({ t: "lfg_join", activity_id: subArg });
    }
    if (subLower === "leave") return net.send({ t: "lfg_leave" });
    return store.log({ kind: "system", text: "Try: /lfg list | join <activity> | leave" });
  }
  if (lower === "storylet" || lower === "explore") return net.send({ t: "storylet_roll" });
  if (lower === "choose" || lower === "pick") {
    const parts = arg.split(/\s+/);
    const sid = parts[0], cid = parts[1];
    if (!sid || !cid) return store.log({ kind: "system", text: "/choose <storylet_id> <choice_id>" });
    return net.send({ t: "storylet_choose", storylet_id: sid, choice_id: cid });
  }
  if (lower === "arena") {
    const subLower = (rest[0] ?? "").toLowerCase();
    if (subLower === "board" || subLower === "leaderboard" || subLower === "top") return net.send({ t: "arena_leaderboard" });
    return net.send({ t: "arena_rank" });
  }
  if (lower === "bounty" || lower === "bounties") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "bounty_list", filter: "active" });
    if (subLower === "mine") return net.send({ t: "bounty_list", filter: "mine" });
    if (subLower === "on_me" || subLower === "on-me") return net.send({ t: "bounty_list", filter: "on_me" });
    if (subLower === "post") {
      const parts = subArg.split(/\s+/);
      const target = parts[0];
      const gold = Number.parseInt(parts[1] ?? "0", 10);
      const reason = parts.slice(2).join(" ");
      if (!target || !Number.isFinite(gold) || gold <= 0 || !reason) return store.log({ kind: "system", text: "/bounty post <target> <gold> <reason>" });
      return net.send({ t: "bounty_post", target, gold, reason });
    }
    if (subLower === "cancel") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/bounty cancel <id>" });
      return net.send({ t: "bounty_cancel", bounty_id: id });
    }
    return store.log({ kind: "system", text: "Try: /bounty list | mine | on_me | post <target> <gold> <reason> | cancel <id>" });
  }
  if (lower === "pet" || lower === "pets") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "pet_list" });
    if (subLower === "catalog") return net.send({ t: "pet_catalog" });
    if (subLower === "acquire" || subLower === "buy") {
      const parts = subArg.split(/\s+/);
      const tid = parts[0];
      const name = parts.slice(1).join(" ") || undefined;
      if (!tid) return store.log({ kind: "system", text: "/pet acquire <template_id> [custom_name]" });
      return net.send({ t: "pet_acquire", template_id: tid, name });
    }
    if (subLower === "summon") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/pet summon <pet_id>" });
      return net.send({ t: "pet_summon", pet_id: id });
    }
    if (subLower === "dismiss") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/pet dismiss <pet_id>" });
      return net.send({ t: "pet_dismiss", pet_id: id });
    }
    if (subLower === "feed") {
      const parts = subArg.split(/\s+/);
      const pid = Number.parseInt(parts[0], 10);
      const iid = Number.parseInt(parts[1], 10);
      if (!Number.isFinite(pid) || !Number.isFinite(iid)) return store.log({ kind: "system", text: "/pet feed <pet_id> <item_instance_id>" });
      return net.send({ t: "pet_feed", pet_id: pid, instance_id: iid });
    }
    if (subLower === "release") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/pet release <pet_id>" });
      if (!confirm("Release this pet permanently?")) return;
      return net.send({ t: "pet_release", pet_id: id });
    }
    return store.log({ kind: "system", text: "Try: /pet list | catalog | acquire <tpl> [name] | summon <id> | dismiss <id> | feed <id> <food_inst> | release <id>" });
  }
  if (lower === "hire" || lower === "hirelings") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "hireling_list" });
    if (subLower === "catalog") return net.send({ t: "hireling_catalog" });
    if (subLower === "hire") {
      const parts = subArg.split(/\s+/);
      const tid = parts[0];
      const gold = Number.parseInt(parts[1] ?? "0", 10);
      if (!tid || !Number.isFinite(gold) || gold <= 0) return store.log({ kind: "system", text: "/hire hire <template_id> <gold_reserve>" });
      return net.send({ t: "hireling_hire", template_id: tid, reserve_gold: gold });
    }
    if (subLower === "dismiss") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/hire dismiss <id>" });
      return net.send({ t: "hireling_dismiss", id });
    }
    if (subLower === "topup" || subLower === "pay") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const gold = Number.parseInt(parts[1] ?? "0", 10);
      if (!Number.isFinite(id) || gold <= 0) return store.log({ kind: "system", text: "/hire topup <id> <gold>" });
      return net.send({ t: "hireling_topup", id, gold });
    }
    return store.log({ kind: "system", text: "Try: /hire list | catalog | hire <template> <gold> | dismiss <id> | topup <id> <gold>" });
  }
  if (lower === "mount" || lower === "mounts") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "mount_list" });
    if (subLower === "summon") {
      const id = Number.parseInt(subArg, 10);
      if (!Number.isFinite(id)) return store.log({ kind: "system", text: "/mount summon <roster_id>" });
      return net.send({ t: "mount_summon", roster_id: id });
    }
    if (subLower === "dismiss") return net.send({ t: "mount_dismiss" });
    if (subLower === "rename") {
      const parts = subArg.split(/\s+/);
      const id = Number.parseInt(parts[0], 10);
      const name = parts.slice(1).join(" ");
      if (!Number.isFinite(id) || !name) return store.log({ kind: "system", text: "/mount rename <id> <name>" });
      return net.send({ t: "mount_rename", roster_id: id, name });
    }
    return store.log({ kind: "system", text: "Try: /mount list | summon <id> | dismiss | rename <id> <name>" });
  }
  if (lower === "mog" || lower === "transmog") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return net.send({ t: "transmog_list" });
    if (subLower === "bind" || subLower === "set") {
      const parts = subArg.split(/\s+/);
      const slot = parts[0];
      const tid = Number.parseInt(parts[1], 10);
      if (!slot || !Number.isFinite(tid)) return store.log({ kind: "system", text: "/mog bind <slot> <template_id>" });
      return net.send({ t: "transmog_bind", slot, appearance_template_id: tid });
    }
    if (subLower === "clear") {
      if (!subArg) return store.log({ kind: "system", text: "/mog clear <slot>" });
      return net.send({ t: "transmog_clear", slot: subArg });
    }
    return store.log({ kind: "system", text: "Try: /mog list | bind <slot> <template_id> | clear <slot>" });
  }
  if (lower === "leaderboard" || lower === "top") {
    const cat = (arg || "kills").toLowerCase();
    if (!["kills", "duels", "gold", "achievements", "guild_xp"].includes(cat)) {
      return store.log({ kind: "system", text: "/top <kills|duels|gold|achievements|guild_xp>" });
    }
    return net.send({ t: "leaderboard", category: cat as "kills" | "duels" | "gold" | "achievements" | "guild_xp" });
  }
  if (lower === "honours" || lower === "honors") {
    return net.send({ t: "honours", character_name: arg || undefined });
  }
  if (lower === "emotes" || lower === "emote_list") return net.send({ t: "emotes_list" });
  // Cross-room chat channels
  if (lower === "trade" || lower === "lfg" || lower === "ooc" || lower === "help_chan" || lower === "newbie") {
    if (!arg) return store.log({ kind: "system", text: `/${lower} <message>` });
    const chan = lower === "help_chan" ? "help" : lower as "trade" | "lfg" | "ooc" | "newbie";
    return net.send({ t: "channel_send", channel: chan, text: arg });
  }
  if (lower === "channels") return net.send({ t: "channel_list" });
  if (lower === "join") {
    const c = arg.toLowerCase() as "trade" | "lfg" | "ooc" | "help" | "newbie";
    if (!["trade", "lfg", "ooc", "help", "newbie"].includes(c)) return store.log({ kind: "system", text: "/join <trade|lfg|ooc|help|newbie>" });
    return net.send({ t: "channel_join", channel: c });
  }
  if (lower === "leave" && arg && ["trade", "lfg", "ooc", "help", "newbie"].includes(arg.toLowerCase())) {
    return net.send({ t: "channel_leave", channel: arg.toLowerCase() as "trade" | "lfg" | "ooc" | "help" | "newbie" });
  }
  if (lower === "alias" || lower === "aliases") {
    const [sub, ...subRest] = rest;
    const subLower = (sub ?? "").toLowerCase();
    const subArg = subRest.join(" ");
    if (!sub || subLower === "list") return showAliases(store, net);
    if (subLower === "add" || subLower === "set") {
      const spaceIdx = subArg.indexOf(" ");
      if (spaceIdx < 0) return store.log({ kind: "system", text: "/alias add <name> <expansion>" });
      const name = subArg.slice(0, spaceIdx);
      const expansion = subArg.slice(spaceIdx + 1);
      return net.send({ t: "alias_set", name, expansion });
    }
    if (subLower === "remove" || subLower === "rm" || subLower === "del") {
      if (!subArg) return store.log({ kind: "system", text: "/alias remove <name>" });
      return net.send({ t: "alias_remove", name: subArg });
    }
    return store.log({ kind: "system", text: "Try: /alias list | add <name> <expansion> | remove <name>" });
  }
  // Emote shortcuts: any known emote key becomes a direct /emote key dispatch.
  // The server owns the catalog; if the key is unknown it returns "unknown_emote".
  if (EMOTE_KEYS.has(lower)) {
    const target = arg || undefined;
    return net.send({ t: "emote", key: lower, target });
  }
  // Unknown — treat as say
  store.log({ kind: "system", text: `Unknown command: ${head}. Try look / n / s / e / w / say <text> / quit.` });
}

function showAliases(store: ClientStore, net: Net): void {
  net.send({ t: "alias_list" });
  const a = store.get().aliases;
  const keys = Object.keys(a);
  if (!keys.length) {
    store.log({ kind: "system", text: "No aliases set. Try /alias add g global" });
    return;
  }
  store.log({ kind: "system", text: `Aliases (${keys.length}):` });
  for (const k of keys.sort()) {
    const val = a[k];
    const kind = val.includes(";") ? "macro" : "alias";
    store.log({ kind: "system", text: `  /${k}  [${kind}]  →  ${val}` });
  }
}

// Emote keys shadowed from server — kept in sync manually with emotes.ts.
// Adding a new emote only requires adding its key here + the catalog entry.
const EMOTE_KEYS = new Set([
  "agree","applaud","beckon","beg","blink","blush","bow","cheer","clap","comfort",
  "cough","cry","curse","curtsy","dance","eye","flex","frown","gasp","glare",
  "greet","grin","hug","kneel","laugh","mutter","nod","ponder","point",
  "pray","roar","salute","scoff","shake","shiver","shrug","sigh","sit","smile",
  "smirk","snicker","spit","stand","tremble","wave","whistle","wink","yawn","yell",
]);

function escape(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c]!));
}
